/**
 * Notification Permission Prompt Component
 * Shows an attractive modal to encourage users to enable push notifications
 */

import React, { useState, useEffect } from 'react';
import { Bell, X, Check, MessageCircle, Users, GraduationCap, Briefcase, BookOpen, Heart } from 'lucide-react';
import { useAuth } from '../core/contexts/AuthContext';
import usePushNotifications from '../hooks/usePushNotifications';

const NotificationPermissionPrompt = () => {
  const { currentUser } = useAuth();
  const { permissionStatus, requestPermission, isLoading } = usePushNotifications();
  const [showPrompt, setShowPrompt] = useState(false);
  const [hasBeenPrompted, setHasBeenPrompted] = useState(false);

  // Check if we should show the prompt
  useEffect(() => {
    if (!currentUser) return;

    // Check if user has already been prompted (stored in localStorage)
    const promptedKey = `notification-prompted-${currentUser.uid}`;
    const wasPrompted = localStorage.getItem(promptedKey);

    // Show prompt if:
    // 1. User is logged in
    // 2. Permission is default (not asked yet)
    // 3. User hasn't been prompted before
    if (permissionStatus === 'default' && !wasPrompted && !hasBeenPrompted) {
      // Delay showing the prompt for better UX
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 2000); // Show after 2 seconds

      return () => clearTimeout(timer);
    }
  }, [currentUser, permissionStatus, hasBeenPrompted]);

  const handleEnableNotifications = async () => {
    try {
      const granted = await requestPermission();
      
      if (granted) {
        setShowPrompt(false);
        // Mark as prompted
        const promptedKey = `notification-prompted-${currentUser.uid}`;
        localStorage.setItem(promptedKey, 'true');
        setHasBeenPrompted(true);
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Mark as prompted even if they dismissed it
    const promptedKey = `notification-prompted-${currentUser.uid}`;
    localStorage.setItem(promptedKey, 'true');
    setHasBeenPrompted(true);
  };

  // Don't show if conditions aren't met
  if (!showPrompt || !currentUser || permissionStatus !== 'default') {
    return null;
  }

  const benefits = [
    {
      icon: <MessageCircle className="w-5 h-5 text-blue-500" />,
      text: "Instant alerts for new messages"
    },
    {
      icon: <Users className="w-5 h-5 text-green-500" />,
      text: "Connection requests from classmates"
    },
    {
      icon: <BookOpen className="w-5 h-5 text-purple-500" />,
      text: "Assignment help and study group invites"
    },
    {
      icon: <Briefcase className="w-5 h-5 text-orange-500" />,
      text: "Graduate job opportunities"
    },
    {
      icon: <GraduationCap className="w-5 h-5 text-indigo-500" />,
      text: "Academic achievement updates"
    },
    {
      icon: <Heart className="w-5 h-5 text-red-500" />,
      text: "Important university announcements"
    }
  ];

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        {/* Modal */}
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-scale-in">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-8 text-center relative">
            <button
              onClick={handleDismiss}
              className="absolute top-4 right-4 text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors"
            >
              <X size={20} />
            </button>
            
            <div className="flex justify-center mb-4">
              <div className="bg-white bg-opacity-20 rounded-full p-4">
                <Bell className="w-8 h-8 text-white" />
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-2">
              Stay Connected!
            </h2>
            <p className="text-green-100 text-sm">
              Never miss what matters most to your university journey
            </p>
          </div>

          {/* Content */}
          <div className="px-6 py-6">
            <p className="text-gray-700 text-center mb-6 leading-relaxed">
              Get instant notifications so you never miss out on:
            </p>

            {/* Benefits List */}
            <div className="space-y-4 mb-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center space-x-3 group">
                  <div className="flex-shrink-0 bg-gray-50 rounded-lg p-2 group-hover:bg-gray-100 transition-colors">
                    {benefit.icon}
                  </div>
                  <span className="text-gray-700 text-sm font-medium">
                    {benefit.text}
                  </span>
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleEnableNotifications}
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold py-4 px-6 rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-200 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Enabling...</span>
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    <span>Yes, Keep Me Updated!</span>
                  </>
                )}
              </button>

              <button
                onClick={handleDismiss}
                className="w-full text-gray-500 font-medium py-3 px-6 rounded-xl hover:bg-gray-100 transition-colors"
              >
                Maybe Later
              </button>
            </div>

            {/* Trust Indicators */}
            <div className="mt-6 pt-4 border-t border-gray-100">
              <div className="flex items-center justify-center space-x-4 text-xs text-gray-500">
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span>Privacy Protected</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span>No Spam</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  <span>Easy to Disable</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default NotificationPermissionPrompt;